import React, {useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import List from 'components/atoms/Lists/List/List';
import ListItem from 'components/atoms/Lists/ListItem/ListItem';
import utils from 'helpers/utils';
import Button from 'components/atoms/Buttons/Button/Button';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Add from '@mui/icons-material/Add';
import constants from 'helpers/constants';
import StyledTeamMappingsForm from 'components/organisms/Forms/TeamMappingsForm/TeamMappingsForm.styles';
import TeamMappingForm from 'components/organisms/Forms/TeamMappingForm/TeamMappingForm';

const TeamMappingsForm = React.forwardRef((props, ref) => {
  const {
    teams,
    connection,
    fieldData,
    required,
    readOnly,
    disabled,
    error,
    errorFrom,
    errorTo,
    multiple,
    onChange,
    onError,
    ...innerProps
  } = useComponentProps({
    multiple: true,
    ...props
  }, 'TeamMappingsForm');

  const innerRef = useRef(null);
  const teamRefs = useRef({});

  const [internalState, setInternalState] = useState({
    teams: []
  });

  const teamMappingsForm = useMemo(() => ({
    refs: {
      ref: innerRef
    },
    submit: (touch = true) => {
      return Promise.all(Object.keys(teamRefs.current).map((teamId) => {
        return teamRefs.current[teamId]?.ref?.submit?.(touch);
      }));
    },
    validate: (touch = true) => {
      return Promise.all(Object.keys(teamRefs.current).map((teamId) => {
        return teamRefs.current[teamId]?.ref?.validate?.(touch);
      }))
        .then((errors) => {
          return errors.reduce((o, f) => ({...o, ...utils.cleanObject(f)}), {});
        });
    },
    reset: (state) => {
      return Promise.all(Object.keys(teamRefs.current).map((teamId) => {
        return teamRefs.current[teamId]?.ref?.reset?.(state);
      }));
    }
  }), []);

  useImperativeHandle(ref, () => teamMappingsForm);

  useLayoutEffect(() => {
    let minId = 0;

    const tms = (teams ?? [])
      .map((s) => {
        if (!utils.isDefined(s.id)) {
          minId = minId - 1;
        } else if (+s.id < 0) {
          minId = Math.min(minId, +s.id);
        }

        return {
          ...s,
          id: !utils.isDefined(s.id) ? minId : +s.id
        }
      });

    if (!readOnly && tms.length === 0) {
      let direction = constants.data.mappingDirections.filter((md) => md.connections.find((c) => c === connection?.type))[0];
      tms.push({id: minId - 1, direction: direction});
    }

    setInternalState(utils.updater({
      teams: tms
    }, true));
  }, [teams, readOnly, connection?.type]);

  const handleAddTeam = () => {
    setInternalState((current) => {
      const direction = constants.data.mappingDirections.filter((md) => md.connections.find((c) => c === connection?.type))[0];
      const minId = current.teams.reduce((i, l) => +l.id < 0 ? Math.min(i, +l.id) : i, 0);

      return {
        ...current,
        teams: current.teams.concat([{id: minId - 1, direction: direction.value}])
      }
    });
  }

  const doErrors = () => {
    const errorIds = Object.keys(teamRefs.current).reduce((o, k) => {
      return teamRefs.current[k].ref && teamRefs.current[k].error ? {...o, [k]: true} : o;
    }, {});

    const error = !utils.isEmpty(errorIds);

    onError?.(error);
    setInternalState(utils.updater({ error, errorIds }, true));
  }

  const handleChange = (team) => (values) => {
    const idx = internalState.teams.findIndex((t) => +t.id === +team.id);
    
    if (idx !== -1) {
      const newValue = [
        ...internalState.teams.slice(0, idx),
        {...team, ...values},
        ...internalState.teams.slice(idx + 1),
      ];

      setInternalState(utils.updater({teams: newValue}, true));
      onChange?.(newValue);
    }
  }

  const handleDelete = (team) => () => {
    const reset = teamRefs.current[team.id]?.ref?.reset;
    delete teamRefs.current[team.id];
    utils.retry(() => {
      reset?.({touched: {}});
    }, 5).then();

    const newValue = internalState.teams
      .filter((l) => +l.id !== +team.id);

    setInternalState(utils.updater({teams: newValue}, true));
    onChange?.(newValue);
    doErrors();
  }

  const handleRef = (team) => (ref) => {
    teamRefs.current[team.id] = {
      ...teamRefs.current[team.id],
      ref
    };
  }

  const handleError = (team) => (error) => {
    teamRefs.current[team.id] = {
      ...teamRefs.current[team.id],
      error
    };

    doErrors();
  }

  const renderTeam = (team, only) => {
    const hasErrorFrom = errorFrom || internalState.teams
      .filter((t) => !internalState.errorIds?.[t.id])
      .filter((mt) => !internalState.errorIds?.[team.id] && mt.from?.id === team.from?.id).length > 1;
    const hasErrorTo = errorTo || internalState.teams
      .filter((t) => !internalState.errorIds?.[t.id])
      .filter((mt) => !internalState.errorIds?.[team.id] && mt.to?.id === team.to?.id).length > 1;

    return <TeamMappingForm ref={handleRef(team)}
                            key={team.id}
                            className="TeamMappingsForm-team"
                            team={team}
                            only={only}
                            fieldData={fieldData}
                            connection={connection}
                            error={error}
                            errorFrom={Boolean(hasErrorFrom)}
                            errorTo={Boolean(hasErrorTo)}
                            required={required || !only}
                            readOnly={readOnly}
                            disabled={disabled}
                            removable={true}
                            hideActions={!multiple}
                            onChange={handleChange(team)}
                            onDelete={handleDelete(team)}
                            onError={handleError(team)}/>
  }

  return <StyledTeamMappingsForm ref={innerRef} {...innerProps}>
    <List gap={8} catchFocus={false}>
      {internalState.teams?.map((team) => {
        return <ListItem key={team.id} data-key={team.id} density="densest">
          {renderTeam(team, internalState.teams.length === 1)}
        </ListItem>
      })}
    </List>
    {(multiple && !(readOnly || disabled)) ?
      <Button variant="text"
              disabled={Boolean(error || errorFrom || errorTo || internalState.error || (internalState.teams.find((f) => utils.isEmpty(f.from?.id) || utils.isEmpty(f.to?.id))))}
              startIcon={<Icon icon={Add} />}
              onClick={handleAddTeam}>
        Add team
      </Button> : null}
  </StyledTeamMappingsForm>
});

TeamMappingsForm.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  teams: PropTypes.array,
  connection: PropTypes.object,
  fieldData: PropTypes.object,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  errorFrom: PropTypes.bool,
  errorTo: PropTypes.bool,
  success: PropTypes.bool,
  multiple: PropTypes.bool,
  onChange: PropTypes.func,
  onError: PropTypes.func
};

export default TeamMappingsForm;
